// ============================================================
// CombatantVitals - the compact "how is this one doing" strip.
//
// Health number, optional bar, and up to N condition pips. This exact
// arrangement was written out by hand in the roster chip bar and the
// combatant picker, with different thresholds and (in the chip bar)
// hard-coded hex, so the same combatant could read green in one place
// and amber in the other.
//
// Takes an already-projected Target, never a Combatant: projection is
// identity-cached on the caller's side and keeping it there is what
// stops this component from quietly invalidating memoized parents.
// ============================================================
import { healthBandLabel, healthColor, vitalsOf } from '../engine/healthRamp';
import { useWoundDisplay } from '../hooks/useWoundDisplay';
import { condColor, decodeCond } from '../engine/conditions';
import type { Target } from '../types';
import clsx from '../utils/clsx';
import styles from '../styles/CombatantVitals.module.css';

export interface CombatantVitalsProps {
  target: Target;
  /** `inline` for a chip or a menu row, `strip` for a panel header or card. */
  layout?: 'inline' | 'strip';
  showBar?: boolean;
  maxPips?: number;
  /** Suffix after the numbers - the picker says "mag" for hordes. */
  hordeSuffix?: string;
  className?: string;
  /** For the pip group's aria-label, e.g. "3 conditions on Ork Nob". */
  name?: string;
}

export default function CombatantVitals({
  target,
  layout = 'inline',
  showBar = false,
  maxPips = 4,
  hordeSuffix = '',
  className,
  name,
}: CombatantVitalsProps) {
  const { shown, total, isHorde } = vitalsOf(target);
  const { showNumbers } = useWoundDisplay();
  const colour = healthColor(shown, total);
  const conditions = target.conditions || [];
  const overflow = conditions.length - maxPips;
  const pct = total > 0 ? Math.max(0, Math.min(100, (Math.max(0, shown) / total) * 100)) : 0;

  return (
    <span className={clsx(styles.vitals, layout === 'strip' && styles.strip, className)}>
      {showBar && (
        <span className={styles.bar} aria-hidden="true">
          <span className={styles.barFill} style={{ width: `${pct}%`, background: colour }} />
        </span>
      )}
      <span className={styles.numbers} style={{ color: colour }}>
        {isHorde ? '⚔ ' : ''}
        {/* The maximum goes with the number: a band beside a known total
            is a bounded range, and the arithmetic comes straight back. */}
        {showNumbers ? `${shown}/${total}` : healthBandLabel(shown, total)}
        {isHorde && hordeSuffix ? ` ${hordeSuffix}` : ''}
      </span>
      {conditions.length > 0 && (
        <span
          className={styles.pips}
          aria-label={`${conditions.length} conditions${name ? ` on ${name}` : ''}`}
          data-testid="vitals-pips"
        >
          {conditions.slice(0, maxPips).map((code, i) => (
            <span
              key={`${code}-${i}`}
              className={styles.pip}
              style={{ background: condColor(code).border }}
              title={decodeCond(code) ?? code}
            />
          ))}
          {overflow > 0 && <span className={styles.more}>+{overflow}</span>}
        </span>
      )}
    </span>
  );
}
